/**
 * De una pista del motor (`kind` + `args`) a las celdas que menciona, para resaltarlas
 * al pulsarla (MVP §6.3). Ejes de la rejilla: `grid[hora][sala] = persona`.
 *
 * Con estos ejes el resaltado sale mucho más fino que con horas × personajes: cinco de
 * las ocho clases apuntan a **una sola casilla**, porque fijan la sala y la hora y lo
 * único que discuten es quién estaba allí.
 *
 * | Clase                    | Qué se resalta                                  |
 * | ------------------------ | ----------------------------------------------- |
 * | `AT`, `NOT_AT`           | la casilla (hora, sala)                         |
 * | `OPEN`, `FIRST_IN`       | la casilla (primera hora, sala)                 |
 * | `CLOSE`, `LAST_IN`       | la casilla (última hora, sala)                  |
 * | `BEFORE`, `JUST_BEFORE`  | la columna de la sala: fijan el orden, no la hora |
 *
 * `OPEN` y `FIRST_IN` coinciden —y `CLOSE` con `LAST_IN`— porque ninguna sala se queda
 * vacía nunca: la primera hora en que una sala se ocupa es siempre la primera del caso.
 * Es la misma nota que el panel de reglas le da al jugador.
 */

import type { Clue } from '@murdoku/engine';
import { parseClue } from '@murdoku/engine';

export interface CellRef {
  hour: number;
  room: number;
}

export interface ClueFocus {
  cells: CellRef[];
  /** Personas mencionadas, para marcarlas también en el selector. */
  people: number[];
  rooms: number[];
}

function wholeColumn(room: number, n: number): CellRef[] {
  return Array.from({ length: n }, (_, hour) => ({ hour, room }));
}

export function clueFocus(clue: Clue, n: number): ClueFocus {
  const parsed = parseClue(clue, n);
  switch (parsed.kind) {
    case 'AT':
    case 'NOT_AT':
      return {
        cells: [{ hour: parsed.hour, room: parsed.room }],
        people: [parsed.person],
        rooms: [parsed.room],
      };
    case 'OPEN':
    case 'FIRST_IN':
      return {
        cells: [{ hour: 0, room: parsed.room }],
        people: [parsed.person],
        rooms: [parsed.room],
      };
    case 'CLOSE':
    case 'LAST_IN':
      return {
        cells: [{ hour: n - 1, room: parsed.room }],
        people: [parsed.person],
        rooms: [parsed.room],
      };
    case 'JUST_BEFORE':
    case 'BEFORE':
      return {
        cells: wholeColumn(parsed.room, n),
        people: [parsed.first, parsed.second],
        rooms: [parsed.room],
      };
  }
}

/** Conjunto de claves `hora:sala` para consultar en O(1) al pintar la rejilla. */
export function focusKeys(focus: ClueFocus | null): ReadonlySet<string> {
  if (focus === null) return EMPTY_KEYS;
  return new Set(focus.cells.map(({ hour, room }) => `${hour}:${room}`));
}

const EMPTY_KEYS: ReadonlySet<string> = new Set<string>();
